'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useInView } from 'react-intersection-observer';
import { getAboutMeImage } from '@/utils/imageSettings';
import styles from './About.module.css';
import { trackEvent } from '@/lib/analytics/firebaseAnalytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { scrollToLandingSection } from '@/utils/scrollToLandingSection';

export type AboutVariant = 'section' | 'page';

interface AboutProps {
  variant?: AboutVariant;
}

const About = ({ variant = 'section' }: AboutProps) => {
  const isPage = variant === 'page';
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [aboutImg, setAboutImg] = useState('');
  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const img = await getAboutMeImage();
        setAboutImg(img);
      } catch (error) {
        console.error('Error loading about image:', error);
      }
    };
    loadImage();
  }, []);

  useEffect(() => {
    if (hasTrackedView) return;
    if (isPage || inView) {
      trackEvent(isPage ? AnalyticsEvents.VIEW_ABOUT_PAGE : AnalyticsEvents.VIEW_ABOUT_SECTION);
      setHasTrackedView(true);
    }
  }, [inView, hasTrackedView, isPage]);

  const handleInstagramClick = () => {
    trackEvent(AnalyticsEvents.CLICK_INSTAGRAM_LINK, {
      source: isPage ? 'about_page' : 'about_section',
    });
  };

  const handleJoinProgramClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent(AnalyticsEvents.CLICK_JOIN_BUTTON, {
      source: isPage ? 'about_page' : 'about_section',
      cta_type: 'join_online_program',
    });
    if (isPage) return;
    e.preventDefault();
    scrollToLandingSection('pricing');
  };

  const HeadingTag = isPage ? 'h1' : 'h2';
  const showContent = isPage || Boolean(aboutImg);

  return (
    <section
      className={`${styles.about} ${isPage ? styles.page : ''}`}
      id="about"
      ref={ref}
    >
      {showContent && (
        <div className={styles.container}>
          <div
            className={`${styles.aboutWrapper} ${inView || isPage ? styles.fadeIn : ''} ${
              aboutImg ? '' : styles.noImage
            }`}
          >
            <div className={styles.aboutHeader}>
              <HeadingTag>{isPage ? 'About Lior Murphy' : 'About Me'}</HeadingTag>
            </div>
            {aboutImg ? (
              <div className={styles.aboutImage}>
                <div className={styles.imagePlaceholder}>
                  <Image
                    src={aboutImg}
                    alt="Lior Murphy"
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    style={{ objectFit: 'cover' }}
                    priority={isPage}
                  />
                </div>
              </div>
            ) : null}
            <div className={styles.aboutBody}>
              <p>
                I&apos;m Lior Murphy. Online I coach backbends and splits: live Zoom classes and an on-demand library. I also teach in person at Moveōm in Tel Aviv, including handstands and calisthenics there.
              </p>
              <p>
                I grew up practicing several sports, stopped around age 13, and returned to training (7 years ago) at 27 with a clearer sense of direction and a more informed approach to working with an adult body. That shaped the way I coach today: developing clear technique, strength that supports it, and control throughout the full range of motion.
              </p>
              <p>
                My work is focused on giving people practical tools and a clear understanding of how their bodies work, so the training feels structured and makes sense.
                I coach both in person and online, working with all levels, from beginners to advanced athletes.
              </p>
              <p>
                My main goal is to help students progress with confidence and build the movement freedom to keep developing independently.
              </p>
              <div className={styles.buttonGroup}>
                <a
                  href="https://www.instagram.com/lior_murphy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.instagramBtn}
                  aria-label="Follow on Instagram"
                  onClick={handleInstagramClick}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                  Follow on Instagram
                </a>
                <Link
                  href={isPage ? '/#pricing' : '#pricing'}
                  className={styles.btn}
                  onClick={handleJoinProgramClick}
                >
                  Join My Online Program
                </Link>
              </div>
              {isPage ? (
                <p>
                  Learn about <Link href="/classes">live Zoom backbend and splits classes</Link>
                  {' '}and the <Link href="/vod">on-demand VOD library</Link>.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default About;
